import {Injectable} from '@angular/core';
import {ParamMap, Params} from '@angular/router';
import {BENCH_TYPES, BenchType, isRequestStatus, REQUEST_STATUSES, RequestStatus} from '../shared/model/bench-entry.model';

export type BenchTypeFilter = 'ALL' | BenchType;
export type BenchAvailabilityFilter = 'ALL' | 'NOW' | 'SOON';
export type BenchSortKey =
  | 'person' | 'type' | 'company' | 'position'
  | 'location' | 'availableFrom' | 'confirmedFrom'
  | 'vacation' | 'offers';
export type BenchSortDirection = 'asc' | 'desc';
export type BenchViewMode = 'table' | 'card';

export interface BenchFilterState {
  search: string;
  /** 'ALL' oder ein Firmenkürzel. Ungeprüft, bis die Übersicht den Wert gegen die Stammdaten normalisiert. */
  company: string;
  type: BenchTypeFilter;
  availability: BenchAvailabilityFilter;
  availableFrom: string | null;
  status: RequestStatus[];
  sortKey: BenchSortKey;
  sortDir: BenchSortDirection;
  viewMode: BenchViewMode;
}

export const BENCH_SORT_KEYS: readonly BenchSortKey[] = [
  'person', 'type', 'company', 'position',
  'location', 'availableFrom', 'confirmedFrom',
  'vacation', 'offers',
];

export const BENCH_FILTER_PARAMS: readonly string[] =
  ['search', 'company', 'type', 'availability', 'availableFrom', 'status', 'sort', 'dir', 'view'];

const AVAILABILITY_VALUES: readonly BenchAvailabilityFilter[] = ['NOW', 'SOON', 'ALL'];
const VIEW_MODES: readonly BenchViewMode[] = ['table', 'card'];

export function isIsoCalendarDate(value: string | null): value is string {
  if (value == null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

export function defaultBenchFilterState(): BenchFilterState {
  return {
    search: '',
    company: 'ALL',
    type: 'ALL',
    availability: 'NOW',
    availableFrom: null,
    status: [],
    sortKey: 'person',
    sortDir: 'asc',
    viewMode: 'table',
  };
}

/** Gemerkte Filterauswahl der Projektakquise — nur im Arbeitsspeicher, also bis zum Ende des Tabs. */
@Injectable({providedIn: 'root'})
export class BenchFilterStateService {
  private state: BenchFilterState = defaultBenchFilterState();

  read(): BenchFilterState {
    return {...this.state, status: [...this.state.status]};
  }

  write(state: BenchFilterState): void {
    this.state = {...state, status: this.canonicalStatus(state.status)};
  }

  hasFilterParams(params: ParamMap): boolean {
    return BENCH_FILTER_PARAMS.some(key => params.has(key));
  }

  /** Jeder unbekannte Wert fällt still auf seinen Default zurück. */
  fromQueryParams(params: ParamMap): BenchFilterState {
    const state = defaultBenchFilterState();
    const type = params.get('type');
    const availability = params.get('availability');
    const sortKey = params.get('sort');
    const sortDir = params.get('dir');
    const viewMode = params.get('view');

    state.search = params.get('search') ?? '';
    state.company = (params.get('company') ?? '').trim() || 'ALL';
    if (this.isTypeFilter(type)) state.type = type;
    if (this.isAvailability(availability)) state.availability = availability;
    const availableFrom = params.get('availableFrom');
    if (isIsoCalendarDate(availableFrom)) {
      state.availableFrom = availableFrom;
      state.availability = 'NOW';
    }
    state.status = this.parseStatus(params.get('status'));
    if (this.isSortKey(sortKey)) state.sortKey = sortKey;
    if (sortDir === 'asc' || sortDir === 'desc') state.sortDir = sortDir;
    if (this.isViewMode(viewMode)) state.viewMode = viewMode;

    return state;
  }

  /** `null` entfernt den Parameter aus der Adresszeile; Defaults werden nie geschrieben. */
  toQueryParams(state: BenchFilterState): Params {
    const status = this.canonicalStatus(state.status);
    return {
      search: state.search.trim() ? state.search : null,
      company: state.company !== 'ALL' ? state.company : null,
      type: state.type !== 'ALL' ? state.type : null,
      availability: !state.availableFrom && state.availability !== 'NOW' ? state.availability : null,
      availableFrom: state.availableFrom ?? null,
      status: status.length ? status.join(',') : null,
      sort: state.sortKey !== 'person' ? state.sortKey : null,
      dir: state.sortDir !== 'asc' ? state.sortDir : null,
      view: state.viewMode !== 'table' ? state.viewMode : null,
    };
  }

  private parseStatus(raw: string | null): RequestStatus[] {
    const values = (raw ?? '').split(',').map(v => v.trim()).filter(isRequestStatus);
    return REQUEST_STATUSES.filter(status => values.includes(status));
  }

  /** Feste Reihenfolge ohne Duplikate, damit derselbe Filter dieselbe Adresszeile ergibt. */
  private canonicalStatus(status: readonly RequestStatus[]): RequestStatus[] {
    return REQUEST_STATUSES.filter(value => status.includes(value));
  }

  private isTypeFilter(value: string | null): value is BenchTypeFilter {
    return value === 'ALL' || (value != null && (BENCH_TYPES as readonly string[]).includes(value));
  }

  private isAvailability(value: string | null): value is BenchAvailabilityFilter {
    return value != null && (AVAILABILITY_VALUES as readonly string[]).includes(value);
  }

  private isSortKey(value: string | null): value is BenchSortKey {
    return value != null && (BENCH_SORT_KEYS as readonly string[]).includes(value);
  }

  private isViewMode(value: string | null): value is BenchViewMode {
    return value != null && (VIEW_MODES as readonly string[]).includes(value);
  }
}
